import { ContextValueType, createContext, useContext } from '@berlin-front/common/composables/context';
import { reactive, ref, computed, unref, watch, toRefs } from '@vue/composition-api';
import { RawLocation } from 'vue-router';
import { event, set } from 'vue-gtag';
import { useMutation, useQuery } from 'vue-query';
import { MaybeRef } from 'vue-query/lib/vue/types';
import { BerlinBffContext } from '@services/BerlinBff';
import { ApiCacheContext } from '@services/ApiCacheContext';
import useFakeProgress from '@berlin-front/common/composables/useFakeProgress';
import useOrAll from '@berlin-front/common/composables/useOrAll';
import useFingerprint from '@berlin-front/common/composables/useFingerprint';
import useErrors from '@composables/useErrors';
import useVuelidate from '@vuelidate/core';
import {
  getErrors,
  regexp,
  email,
  REG_PHONE_ONLY_NUMBER_STYLE,
  REG_PHONE_ONLY_NUMBER_STYLE_ERR_MSG,
  required,
  maxLength,
} from '@berlin-front/common/composables/validator';
import { ErrorResponse, AtoneMemberTypePostResponseMemberTypeEnum } from '@netprotections/berlin-bff';
import { AxiosError } from 'axios';
import { MemberRequestContext } from '@views/member/MemberRequestContext';
// import { event, set } from 'vue-gtag';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import { ROUTE_NAME } from '@/router/route-name';
import { getRefDeep, isAxiosError, randomString, notNil } from '@berlin-front/common/utils';
import useIsLegacy from '@composables/useIsLegacy';
import { ModalBridgeContext } from '@services/ModalBridgeContext';

export const useLiteAuthLoginGuest = () => {
  const [isLegacy] = useIsLegacy();
  const vueQueryParam = ref(randomString(10));
  const router = useRouter();
  const { registerMemberFormState: state, normalize } = useContext(MemberRequestContext);
  const { atoneLiteAuthModalParameter } = useContext(ApiCacheContext);
  const apiCacheContext = useContext(ApiCacheContext);
  const globals = useContext(ModalBridgeContext);
  const nextPage = ref<RawLocation | undefined>();
  const { napoleon, member } = useContext(BerlinBffContext);
  const termsConsentCheck = ref(false);
  const fingerprint = useFingerprint();
  const { errors, clearAll } = useErrors({
    fieldMapper: {},
    defaultFieldName: 'flashMessages',
  });

  const MEMBER_TYPE = {
    NEW: AtoneMemberTypePostResponseMemberTypeEnum.NUMBER_0,
    NP_MEMBER: AtoneMemberTypePostResponseMemberTypeEnum.NUMBER_1,
    MEMBER: AtoneMemberTypePostResponseMemberTypeEnum.NUMBER_2,
    GUEST: AtoneMemberTypePostResponseMemberTypeEnum.NUMBER_3,
  } as const;

  /**
   * それぞれのuseQueryの結果（isLoading, isSuccessなど）をクリアして初期状態にします。
   */
  const resetUseQuery = () => {
    // TanStackQuery（vueQueryやreactQuery)はクエリキーが変わると異なるリクエストとして状態を返すため
    // 動的クエリキーを変更することで初期化状態に戻します。
    vueQueryParam.value = randomString(10, { blackList: [vueQueryParam.value] });
  };
  // 会員種別特定結果に応じて次の画面が変わる
  const memberType = computed(() => apiCacheContext.atoneMemberTypePostResult.value?.memberType);
  const showMessageToNpLogin = computed(() => memberType.value === MEMBER_TYPE.NP_MEMBER);
  const showMessageToAtoneLogin = computed(() => memberType.value === MEMBER_TYPE.MEMBER);
  const isTrusted = ref(false);

  const withdrawErrorFlag = ref(false);
  const guestWithdrawErrorFlag = ref(false);

  const resurrectionFaq = process.env.VUE_APP_ATONE_RESURRECTION_FAQ;

  /**
   * 会員種別特定
   * 条件：onSubmit
   */
  const { isSuccess: successMemberTypePost, mutate } = useMutation(
    reactive(['atone.MemberTypePost', { vueQueryParam }]),
    () =>
      member.atoneMemberControllerMemberTypePost(getRefDeep(apiCacheContext.tokens, 'requestToken'), {
        mailAddress: state.mailAddress,
      }),
    {
      retry: false,
      onSuccess: ({ data }) => {
        apiCacheContext.setAtoneMemberTypePostResult(data);
      },
    },
  );

  /**
   * 仮会員ログイン（既存仮会員）
   */
  const guestLoginExecFlag = computed(() => memberType.value === MEMBER_TYPE.GUEST && unref(successMemberTypePost));

  const { isSuccess: successGuestAuthenticatePost, isError: errorGuestAuthenticatePost } = useQuery(
    reactive(['napoleon.guestAuthenticate', { vueQueryParam }]),
    () =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      napoleon.guestControllerAuthenticatePost(atoneLiteAuthModalParameter.value.pubKey, {
        mailAddress: state.mailAddress,
        phoneNumber: state.phoneNumber,
      }),
    {
      enabled: guestLoginExecFlag,
      onSuccess: ({ data }) => {
        apiCacheContext.setGuestAuthenticatePostResult(data);
        apiCacheContext.setAtoneLiteGuestLoginAccount({
          phoneNumber: state.phoneNumber,
          email: state.mailAddress,
        });
      },
    },
  );

  /**
   * フィンガープリントチェック（既存仮会員）
   * 仮会員認証が終わったタイミングで実行する
   * onSuccess: 信用の場合、ログインAPIを実行。未信用の場合、SMS送信へ
   */

  const guestFingerprintPostExecFlag = computed(
    () => successMemberTypePost && unref(successGuestAuthenticatePost) && memberType.value === MEMBER_TYPE.GUEST,
  );
  const { isSuccess: successGuestFingerprintPost, isError: errorGuestFingerprintPost } = useQuery(
    reactive(['lite.guestFingerprintPost', { vueQueryParam }]),
    () =>
      napoleon.guestControllerFingerprintPost(
        getRefDeep(apiCacheContext.tokens, 'guestRequestToken'),
        atoneLiteAuthModalParameter.value.pubKey,
        { fingerPrint: fingerprint.value },
      ),
    {
      enabled: guestFingerprintPostExecFlag,
      onSuccess: ({ data }) => {
        apiCacheContext.setGuestFingerprintPostResult(data);
        const { trustedFlag } = data;
        if (trustedFlag) {
          isTrusted.value = true;
          // 認証完了画面に遷移
          nextPage.value = {
            name: ROUTE_NAME.ATONE_LITE_AUTH_COMPLETE,
          };
        } else {
          // SMS認証画面に遷移
          nextPage.value = {
            name: ROUTE_NAME.ATONE_LITE_AUTH_EXIST_GUEST_SMS_AUTH,
          };
        }
      },
    },
  );

  /**
   * 認証トークン取得（既存仮会員）
   */
  const AuthTokenGetExecFlag = computed(() => successGuestFingerprintPost && isTrusted.value);
  const { isSuccess: successGuestAuthTokenGet, isError: errorGuestAuthTokenGet } = useQuery(
    reactive(['lite.guestAuthenticationTokenGet', { vueQueryParam }]),
    () =>
      napoleon.guestControllerAuthenticationTokenGet(
        atoneLiteAuthModalParameter.value.pubKey,
        getRefDeep(apiCacheContext.tokens, 'trustedTrackToken'),
        getRefDeep(apiCacheContext.tokens, 'guestRequestToken'),
        globals.requestId,
        atoneLiteAuthModalParameter.value.payment.userNo,
        isLegacy.value,
      ),
    {
      enabled: AuthTokenGetExecFlag,
      onSuccess: ({ data }) => {
        apiCacheContext.setGuestAuthenticationTokenGetResult(data);
        apiCacheContext.setNpTokenAndSignature(data.token, data.signature);

        set({
          user_id: notNil(data.memberCode),
          user_properties: {
            member_id: notNil(data.memberCode),
            shop_id: notNil(data.shopId),
            // shop_name: notNil(data.shopName),
            guest: 'yes',
          },
        });

        event('login', {
          method: 'Guest Lite',
        });
      },
    },
  );

  /**
   * SMS送信（既存仮会員）
   */
  const guestRegisterSmsNotifyExecFlag = computed(
    () =>
      unref(successMemberTypePost) &&
      unref(successGuestFingerprintPost) &&
      !isTrusted.value && // 未信用
      memberType.value === MEMBER_TYPE.GUEST,
  ) as unknown as MaybeRef<boolean | undefined>;

  const {
    isSuccess: successExistingGuestSmsNotifyPost,
    isError: errorExistingGuestSmsNotifyPost,
    isFetched: fetchedExistingGuestSmsNotifyPost,
  } = useQuery(
    reactive(['lite.existSmsNotifyPost', { vueQueryParam }]),
    () =>
      napoleon.guestControllerSmsAuthenticateNotify(
        getRefDeep(apiCacheContext.tokens, 'guestRequestToken'),
        atoneLiteAuthModalParameter.value.pubKey,
        {
          resend: false,
        },
      ),
    {
      enabled: guestRegisterSmsNotifyExecFlag,
      onSuccess: ({ data }) => {
        apiCacheContext.setGuestSmsAuthenticateNotifyPostResult(data);
      },
    },
  );

  /**
   * 利用申請チェック（新規仮会員）
   * タイミング：memberTypeがNewの場合に実行される
   */
  const guestValidateExecFlag = computed(
    () => unref(successMemberTypePost) && memberType.value === MEMBER_TYPE.NEW,
  ) as unknown as MaybeRef<boolean | undefined>;

  const { isSuccess: successGuestRegisterCheck, isError: errorGuestRegisterCheck } = useQuery(
    reactive(['lite.guestValidate', { vueQueryParam }]),
    () =>
      napoleon.guestControllerValidatePost(
        getRefDeep(apiCacheContext.tokens, 'requestToken'),
        atoneLiteAuthModalParameter.value.pubKey,
        {
          phoneNumber: state.phoneNumber,
          mailAddress: state.mailAddress,
        },
      ),
    {
      enabled: guestValidateExecFlag,
      onSuccess: () => {
        apiCacheContext.setAtoneLiteNewAccount({
          phoneNumber: state.phoneNumber,
          email: state.mailAddress,
        });
      },
      onError: (e: AxiosError<ErrorResponse>) => {
        // 退会電話番号と合致した場合の対応。リンク付きメッセージを表示するために特殊な対応を入れている。
        const withdrawErrorCode = 'EATN0435';
        const guestWithdrawErrorCode = 'EATN0474';
        if (isAxiosError(e)) {
          const details = e.response?.data.details;
          if (typeof details !== 'string') {
            if (details?.errors.some((error) => error.code === withdrawErrorCode)) withdrawErrorFlag.value = true;
            if (details?.errors.some((error) => error.code === guestWithdrawErrorCode)) {
              guestWithdrawErrorFlag.value = true;
            }
          }
        }
      },
    },
  );

  /**
   * SMS送信（新規仮会員）
   */
  const newGuestSmsNotifyExecFlag = computed(
    () => unref(successMemberTypePost) && memberType.value === MEMBER_TYPE.NEW && unref(successGuestRegisterCheck),
  ) as unknown as MaybeRef<boolean | undefined>;

  const {
    isSuccess: successNewSmsNotifyPost,
    isError: errorNewSmsNotifyPost,
    isFetched: fetchedNewSmsNotifyPost,
  } = useQuery(
    reactive(['member.SmsNotifyPost', { vueQueryParam }]),
    () =>
      member.atoneMemberControllerMemberSmsNotify(
        getRefDeep(apiCacheContext.tokens, 'requestToken'),
        {
          mailAddress: state.mailAddress,
          phoneNumber: state.phoneNumber,
          resend: false,
        },
        atoneLiteAuthModalParameter.value.pubKey,
      ),
    {
      enabled: newGuestSmsNotifyExecFlag,
      onSuccess: ({ data }) => {
        apiCacheContext.setAtoneSmsNotifyPostResult(data);
        nextPage.value = {
          name: ROUTE_NAME.ATONE_LITE_AUTH_NEW_GUEST_SMS_AUTH,
          query: {
            previous: ROUTE_NAME.ATONE_LITE_AUTH_GUEST_LOGIN,
          },
        };
      },
      onError: () => {
        // XXX: このコメントは？
        // 送信に失敗した場合、入力された電話番号をセット
        // apiCacheContext.setAtoneMemberSmsNotifyResult({ phoneNumber: state.phoneNumber });
        nextPage.value = {
          name: ROUTE_NAME.ATONE_LITE_AUTH_NEW_GUEST_SMS_AUTH,
          query: {
            previous: ROUTE_NAME.ATONE_LITE_AUTH_GUEST_LOGIN,
          },
        };
      },
    },
  );

  // バリデーション
  const rules = computed(() => ({
    phoneNumber: {
      required,
      regexp: regexp(REG_PHONE_ONLY_NUMBER_STYLE, REG_PHONE_ONLY_NUMBER_STYLE_ERR_MSG),
    },

    mailAddress: {
      required,
      maxLength: maxLength(256),
      email,
    },
  }));
  const v$ = useVuelidate(rules, state);
  const messages = computed(() => unref(errors)?.flashMessages ?? []);

  const nextIsMemberLogin = computed(() => unref(successMemberTypePost) && memberType.value === MEMBER_TYPE.MEMBER);
  const nextIsNpMemberLogin = computed(
    () => unref(successMemberTypePost) && memberType.value === MEMBER_TYPE.NP_MEMBER,
  );
  const nextIsGuestLinkLoading = computed(
    () =>
      unref(successMemberTypePost) &&
      memberType.value === MEMBER_TYPE.GUEST &&
      isTrusted.value &&
      successGuestAuthTokenGet.value,
  );

  const isSuccess = useOrAll(
    successNewSmsNotifyPost, // 新規仮会員・SMS送信後
    successExistingGuestSmsNotifyPost, // 既存仮会員・未信用状態・SMS送信後
    nextIsGuestLinkLoading, // 既存仮会員・信用状態
    nextIsMemberLogin,
    nextIsNpMemberLogin,
  );
  const isError = useOrAll(
    errorNewSmsNotifyPost,
    errorExistingGuestSmsNotifyPost,
    errorGuestRegisterCheck,
    errorGuestFingerprintPost,
    errorGuestAuthenticatePost,
    errorGuestAuthTokenGet,
  );
  const isLoading = ref(false);
  const isEnded = useOrAll(isSuccess, isError);

  const {
    progress,
    completed: progressCompleted,
    isLoading: progressIsLoading,
  } = toRefs(useFakeProgress(5000, isLoading, isEnded));

  // submitによる画面遷移の条件
  // 1. 新規仮会員: SMS送信が完了（送信の成否は問わない）
  // 2. 既存仮会員: FPが有効 or SMS送信が完了（送信の成否は問わない）
  const transitionNextPage = computed(
    () =>
      nextIsGuestLinkLoading ||
      (unref(fetchedNewSmsNotifyPost) && memberType.value === MEMBER_TYPE.NEW) ||
      (unref(fetchedExistingGuestSmsNotifyPost) && memberType.value === MEMBER_TYPE.GUEST),
  );
  watch(progressCompleted, (result) => {
    if (result) {
      if (unref(transitionNextPage) && nextPage.value) {
        void router.replace(nextPage.value);
        return;
      }
    }
    isLoading.value = false;
  });

  // 同意無しにSubmitされるのを防ぐ
  const submitButtonDisabled = computed(() => !termsConsentCheck.value || v$.value.$error);

  const resetBeforeMutate = () => {
    // 会員種別をリセット
    apiCacheContext.setAtoneMemberTypePostResult(undefined);
    // 退会済みフラグをリセット
    withdrawErrorFlag.value = false;
    guestWithdrawErrorFlag.value = false;
    // useQueryのloading等のステータスをクリア。
    resetUseQuery();
  };

  // submitできる条件
  // 1. validationでエラーがないこと
  // 2. submitボタンがdisableされていないこと
  // 3. すでにmutate後ではないこと
  // 4. 実行中ではないこと
  const onSubmit = async () => {
    // 会員の状態はリセット
    resetBeforeMutate();
    if ((await v$.value.$validate()) && !submitButtonDisabled.value && !unref(isSuccess) && !unref(progressIsLoading)) {
      clearAll();
      isLoading.value = true;
      mutate();
    }
  };

  return reactive({
    state,
    progress,
    progressIsLoading,
    v$,
    messages,
    normalize,
    showMessageToNpLogin,
    showMessageToAtoneLogin,
    getError: getErrors(v$),
    termsConsentCheck,
    submitButtonDisabled,
    onSubmit,
    resurrectionFaq,
    withdrawErrorFlag,
    guestWithdrawErrorFlag,
  });
};

export const LiteLoginGuestContext = createContext({ useLiteAuthLoginGuest });
export type LiteLoginGuestContextType = ContextValueType<typeof LiteLoginGuestContext>;
